import { Component, EventEmitter, HostListener, inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiService, PaymentV2Service, UtilService, VariablesService, VwoService } from '../../shared/services';
import { IPaymentSipConfig } from '../payment-sip/payment-sip.model';
import { IUser } from 'src/app/shared/models';

@Component({
  selector: 'app-payment-sip-upi',
  templateUrl: './payment-sip-upi.component.html',
  styleUrls: ['./payment-sip-upi.component.scss']
})
export class PaymentSipUpiComponent implements OnInit, OnChanges {
  readonly vars = inject(VariablesService);

  @Input() config: IPaymentSipConfig;
  @Input() showTime = true;
  @Output() dataEmit = new EventEmitter();

  upiInputControl = new FormControl('', [Validators.required, Validators.pattern(this.vars.sipUpiRegex)]);
  upiForm: FormGroup;
  filteredOptions: Observable<string[]>;
  isLoading;
  verified;
  checkForValidUpiId = true;

  checkPrefillUPI = '';
  userData: IUser;
  recommendedVpa = '';
  vpaType: 'custom' | 'phone' | 'email' | 'lastused' = 'custom';

  inputFieldData = {
    vpa: ''
  };

  sessionCart;

  constructor(
    private api: ApiService,
    public util: UtilService,
    public paymentV2: PaymentV2Service,
    public vwo: VwoService
  ) { }

  ngOnChanges() {
    this.fillUpiId(this.config);
    if (this.config.one_time_donation) {
      this.upiInputControl.clearValidators();
      this.upiInputControl.addValidators([Validators.required, Validators.pattern(/^\w.+@\w+$/)]);
    }
  }

  ngOnInit() {
    this.upiForm = new FormGroup({
      upi: this.upiInputControl
    });
    this.sessionCart = this.config?.user?.id ? this.util.storage.getFromSession(`${this.config?.user?.id}SipCart`) : '';
    this.submitForm();
    // this.paymentV2.paymentConfig.subscribe((config) => this.fillUpiId(config));
    this.filteredOptions = this.upiInputControl.valueChanges.pipe(
      startWith(''),
      map(value => this.vars.sipUpiOptions.filter(option => option.includes(value.toLowerCase().split('@')[1])))
    );
    this.upiInputControl.valueChanges.subscribe(val => {
      if (this.inputFieldData.vpa === val) return;
      this.inputFieldData.vpa = val;
      this.onVpaFocusOut();
    });
  }

  submitForm() {
    this.paymentV2.pledgeBtn.subscribe(tab => {
      if (tab === 'upi' && !this.vars.isAgent) {
        if (this.upiForm.valid && this.verified) {
          const emitPayload = {
            mode: this.config.selectedTab.order_mode,
            mode_value: 'VPA',
            VPA: this.upiInputControl.value,
            origin_vpa: 'payment_form'
          };
          if (this.vars.isAutoLogin) {
            emitPayload['vpa_type'] = this.vpaType;
          }
          this.dataEmit.emit(emitPayload);
        } else {
          this.util.checkValidForm(this.upiForm);
        }
      }
    });
  }

  async fillUpiId(config) {
    if (!this.upiInputControl.value) {
      let prefillUPI = '';
      if (config.lastOrder && config.lastOrder.upi_id) {
        prefillUPI = config.lastOrder.upi_id;
        this.vpaType = 'lastused';
      } else if (this.config?.paymentOptions?.options?.RECOMMENDED_VPA && !this.vars.isAutoLogin) {
        prefillUPI = this.config?.paymentOptions?.options?.RECOMMENDED_VPA;
      } else if (config.user?.phone_1 && this.checkForValidUpiId && !this.vars.isAutoLogin) {
        for (const item of ['@ybl', '@upi', '@apl']) {
          this.checkForValidUpiId = false;
          const phoneNumber = config.user?.phone_1?.replace(/^0+/, '') + item;
          const isVerified = await this.verifyUpi(phoneNumber);
          if (isVerified) {
            this.vpaType = 'phone';
            this.upiInputControl.setValue(phoneNumber);
            this.verified = true;
            return isVerified;
          }
        }
      }
      if (prefillUPI && this.checkPrefillUPI !== prefillUPI && !this.verified) {
        this.checkPrefillUPI = prefillUPI;
        const isVerified = await this.verifyUpi(prefillUPI);
        if (isVerified) {
          this.recommendedVpa = prefillUPI;
          this.upiInputControl.setValue(prefillUPI);
          return isVerified;
        }
      }
    }
  }

  verifyUpi(value): boolean {
    // return new Promise((resolve, reject) => {
    //   try {
    //     this.isLoading = true;
    //     this.api.post(environment.APP.API_URLS.VPA_VALIDATION, { vpa: value }, false).subscribe((res) => {
    //       this.isLoading = false;
    //       this.verified = res.data && res.data.isVPAValid ? true : false;
    //       this.upiForm.controls.upi.setErrors({ invalid: this.verified ? null : true });
    //       if (this.verified) {
    //         this.upiForm.controls.upi.updateValueAndValidity();
    //       }
    //       resolve(this.verified);
    //     });
    //   } catch (error) {
    //     reject(false);
    //   }
    // });
    this.upiInputControl.updateValueAndValidity();
    this.verified = true;
    return true;
  }

  @HostListener('blur', [])
  onInputBlur() {
    if (!this.verified && this.upiInputControl.valid) {
      this.verifyUpi(this.upiInputControl.value);
    }
  }

  upiInputSubmit(data) {
    const emitPayload = {
      mode: this.config.selectedTab.order_mode,
      mode_value: 'VPA',
      VPA: data?.upi,
      vpa_type: data?.vpa_type,
      origin_vpa: 'payment_form'
    };
    this.dataEmit.emit(emitPayload);
  }

  onVpaFocusOut() {
    if (this.upiInputControl?.value === this.recommendedVpa) {
      this.updateValidation('vpa', true);
      this.vpaType = 'lastused';
    } else {
      this.vpaType = 'custom';
      this.updateValidation('vpa', true, this.vars.sipUpiRegex);
    }
  }

  updateValidation(field: 'vpa', require: boolean, pattern?: any) {
    const validators = [];
    if (require) validators.push(Validators.required);
    if (pattern) validators.push(Validators.pattern(pattern));
    if (field === 'vpa') {
      this.upiInputControl.clearValidators();
      this.upiInputControl.addValidators(validators);
      this.upiInputControl.updateValueAndValidity();
    }
  }
}
